/**
 * Employee Performance Scoring Engine
 *
 * Computes a composite performance score (0-100) for each employee
 * by aggregating data from attendance, tasks, and leaves.
 *
 * Weights:
 *   Attendance %     → 30%
 *   Punctuality %    → 15%  (100 - late arrival %)
 *   Task completion  → 25%
 *   Leave management → 15%  (100 - excessive leave %)
 *   Extra effort     → 15%  (overtime contribution)
 */

import { http } from "./http"

/**
 * Fetch attendance summary for a given month/year/company.
 * Reuses the /reports/attendance-summary endpoint that the
 * existing AttendanceSummaryReport page calls.
 */
export async function fetchAttendanceSummary(year, month, companyId = "") {
    // Zero-pad month to ensure proper date format (e.g. 2026-04-01 not 2026-4-01)
    const mm = String(month).padStart(2, "0")
    const url = `/reports/attendance-summary?company_id=${companyId}&date=${year}-${mm}-01&export_mode=false`
    const res = await http.get(url)
    return res.data.result || []
}

/**
 * Fetch project tasks.
 * Returns all tasks so we can calculate per-employee completion rates.
 */
export async function fetchAllTasks() {
    const res = await http.get("/project-tasks")
    return res.data.result || []
}

/**
 * Build performance data for all employees.
 *
 * @param {number} year
 * @param {number} month
 * @param {string} companyId  (optional filter)
 * @returns {Array<Object>}  Scored employee rows
 */
export async function buildPerformanceReport(year, month, companyId = "") {
    // 1. Fetch attendance summary
    const attendance = await fetchAttendanceSummary(year, month, companyId)

    // 2. Fetch tasks (parallel is fine)
    let tasks = []
    try {
        tasks = await fetchAllTasks()
    } catch { /* tasks unavailable — skip */ }

    // 3. Index tasks by employee name for matching
    const tasksByEmployee = {}
    for (const t of tasks) {
        const assignees = t.assignees || t.users || []
        for (const a of assignees) {
            const name = a.fullName || `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.username || ""
            if (!name) continue
            if (!tasksByEmployee[name]) tasksByEmployee[name] = { total: 0, completed: 0, inProgress: 0 }
            tasksByEmployee[name].total++
            const status = (t.status?.name || t.statusName || "").toLowerCase()
            if (status.includes("complete") || status.includes("done") || status.includes("closed")) {
                tasksByEmployee[name].completed++
            } else if (status.includes("progress") || status.includes("active")) {
                tasksByEmployee[name].inProgress++
            }
        }
    }

    // 4. Score each employee
    const employees = attendance.map((emp, idx) => {
        const workingDays = parseNum(emp["Working Days"])
        const present = parseNum(emp["Total days Present"])
        const absent = parseNum(emp["Total days Absent"])
        const leaveDays = parseNum(emp["Total leave Days"])
        const holidays = parseNum(emp["Total Holidays"])
        const otHours = parseNum(emp["Overtime Hours (if applicable)"])
        const lateComing = parseNum(emp["Late Coming"])
        const earlyGoing = parseNum(emp["Early Going"])
        const name = emp["NAME OF EMPLOYEE"] || emp["Name"] || `Employee ${idx + 1}`
        const designation = emp["Designation"] || "—"
        const company = emp["Company"] || "—"

        // --- Attendance Score (30%) ---
        const attendancePct = workingDays > 0 ? (present / workingDays) * 100 : 0
        const attendanceScore = Math.min(attendancePct, 100)

        // --- Punctuality Score (15%) ---
        const latePct = present > 0 ? (lateComing / present) * 100 : 0
        const punctualityScore = Math.max(100 - latePct, 0)

        // --- Task Completion Score (25%) ---
        const empTasks = tasksByEmployee[name] || { total: 0, completed: 0, inProgress: 0 }
        let taskScore = 50 // default if no tasks assigned
        if (empTasks.total > 0) {
            taskScore = (empTasks.completed / empTasks.total) * 100
            // Partial credit for in-progress tasks
            taskScore += (empTasks.inProgress / empTasks.total) * 25
            taskScore = Math.min(taskScore, 100)
        }

        // --- Leave Management Score (15%) ---
        const totalAvailDays = workingDays > 0 ? workingDays : 22 // fallback
        const leavePct = (leaveDays / totalAvailDays) * 100
        // Penalize excessive leaves (>10% is problematic)
        let leaveScore = 100
        if (leavePct > 20) leaveScore = 40
        else if (leavePct > 15) leaveScore = 60
        else if (leavePct > 10) leaveScore = 75
        else if (leavePct > 5) leaveScore = 90

        // --- Extra Effort Score (15%) ---
        // Overtime up to 20 hours = full credit
        const effortScore = Math.min((otHours / 20) * 100, 100)

        // --- Composite Score ---
        const composite = (
            attendanceScore * 0.30 +
            punctualityScore * 0.15 +
            taskScore * 0.25 +
            leaveScore * 0.15 +
            effortScore * 0.15
        )

        return {
            srNo: emp["Sr No"] || idx + 1,
            name,
            designation,
            company,
            workingDays,
            present,
            absent,
            leaveDays,
            holidays,
            otHours,
            lateComing,
            earlyGoing,
            scores: {
                attendance: round(attendanceScore),
                punctuality: round(punctualityScore),
                tasks: round(taskScore),
                leaveManagement: round(leaveScore),
                effort: round(effortScore),
                composite: round(composite),
            },
            taskStats: empTasks,
        }
    })

    // Sort by composite score descending
    employees.sort((a, b) => b.scores.composite - a.scores.composite)

    // Assign rank
    employees.forEach((emp, i) => {
        emp.rank = i + 1
    })

    return employees
}

/**
 * Get summary stats from an array of scored employees
 */
export function getSummaryStats(employees) {
    if (!employees.length) return null

    const scores = employees.map(e => e.scores.composite)
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length
    const top = employees.filter(e => e.scores.composite >= 80)
    const good = employees.filter(e => e.scores.composite >= 60 && e.scores.composite < 80)
    const needsImprovement = employees.filter(e => e.scores.composite < 60)

    return {
        totalEmployees: employees.length,
        avgScore: round(avg),
        topPerformers: top.length,
        goodPerformers: good.length,
        needsImprovement: needsImprovement.length,
        highest: employees[0],
        lowest: employees[employees.length - 1],
    }
}

/**
 * Get score grade label + color
 */
export function getGrade(score) {
    if (score >= 90) return { label: "Excellent", color: "#059669", bg: "rgba(16,185,129,0.1)" }
    if (score >= 80) return { label: "Very Good", color: "#10b981", bg: "rgba(16,185,129,0.08)" }
    if (score >= 70) return { label: "Good", color: "#3b82f6", bg: "rgba(59,130,246,0.08)" }
    if (score >= 60) return { label: "Average", color: "#f59e0b", bg: "rgba(245,158,11,0.08)" }
    if (score >= 50) return { label: "Below Avg", color: "#f97316", bg: "rgba(249,115,22,0.08)" }
    return { label: "Needs Improvement", color: "#ef4444", bg: "rgba(239,68,68,0.08)" }
}

// Helpers
function parseNum(val) {
    const n = parseFloat(val)
    return isNaN(n) ? 0 : n
}

function round(val) {
    return Math.round(val * 10) / 10
}
